import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import styles from "./editOCR.module.scss";
import { handleGetRequest } from "../../../services/GetTemplate";

function EditOCRPackage({ onSubmit, editData }) {
    const [couriers, setCouriers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [billTo, setBillTo] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);

    // Only use editData for edit mode
    const [initialValues, setInitialValues] = useState({
        description: "",
        quantity: 1,
        packageName: "",
        courierName: "",
        trackingNumber: "",
        courier: "",
        createdBy: "",
        creatorType: "",
        billTo: "",
        id: "",
        billToName: "",
        arrivalTime: "",
        merchant: "",
        deliveryInfo: "",
        consigneeType: "",
        consigneeName: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [couriersResult, usersResult] = await Promise.all([
                    handleGetRequest("/userRole?role=courier"),
                    handleGetRequest("/clientAndAdmins")
                ]);

                setCouriers(couriersResult.data);

                const userOptions = usersResult.data.map((user) => ({
                    value: user._id,
                    label: `${user.firstName} ${user.lastName}`,
                    ...user,
                }));
                setAllUsers(userOptions);

                if (editData) {
                    // Edit mode initialization only
                    const matchedBillTo = userOptions.find((opt) => opt.value === editData.billTo);
                    setBillTo(matchedBillTo);

                    setInitialValues({
                        description: editData.description || "",
                        quantity: editData.quantity || 1,
                        packageName: editData.packageName || "",
                        courierName: editData.courierName || "",
                        trackingNumber: editData.trackingNumber || "",
                        courier: editData.courier || "",
                        createdBy: editData.createdBy || "",
                        creatorType: editData.creatorType || "",
                        billTo: editData.billTo || "",
                        id: editData._id || "",
                        billToName: matchedBillTo?.label || "",
                        arrivalTime: editData.arrivalTime || "",
                        merchant: editData.merchant || "",
                        deliveryInfo: editData.deliveryInfo || "",
                        consigneeType: editData.consigneeType || "",
                        consigneeName: editData.consigneeName || "",
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [editData]);

    const validationSchema = Yup.object({
        // Add required validation as needed for editing
        // description: Yup.string().required("Description is required"),
        // quantity: Yup.number().min(1, "Quantity must be at least 1").required("Quantity is required"),
        // packageName: Yup.string().required("Package name is required"),
        // trackingNumber: Yup.string().required("Tracking number is required"),
        // courier: Yup.string().required("Courier is required"),
        // billTo: Yup.string().required("Bill to is required"),
    });

    const handleSubmit = (values, { resetForm }) => {
        if (!onSubmit) return;

        const submitValues = { ...values };

        submitValues.consigneeName = submitValues.packageName;

        // For edit, always use selected BillTo option if set
        if (billTo) {
            submitValues.billTo = billTo.value;
            submitValues.billToName = billTo.label;
        }

        onSubmit(submitValues, true); // true for edit
    };

    const handleBillToChange = (selectedOption, setFieldValue) => {
        setFieldValue("billTo", selectedOption.value);
        setFieldValue("billToName", selectedOption.label);
        setFieldValue("deliveryInfo", selectedOption.deliveryInfo || "");
        setFieldValue("consigneeType", selectedOption.role || "client");
        setBillTo(selectedOption);
    };

    const handleCourierChange = (e, setFieldValue) => {
        const selectedCourier = couriers.find((c) => c._id === e.target.value);
        const courierName = selectedCourier?.courier?.businessName || `${selectedCourier?.name?.firstName} ${selectedCourier?.name?.lastName}`;

        setFieldValue("courier", e.target.value);
        setFieldValue("courierName", courierName);
    };

    const handleTypeChange = (e, setFieldValue) => {
        setFieldValue("consigneeType", e.target.value);
    };

    // Modal functions
    const handleViewImage = () => {
        setShowImageModal(true);
    };

    const handleCloseImageModal = () => {
        setShowImageModal(false);
    };

    return (
        <div className={styles.container}>
            <div className={styles.imageContainer}>
                <button type="button" onClick={handleViewImage}>View Image</button>
            </div>
            {showImageModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <button className={styles.closeButton} onClick={handleCloseImageModal}>×</button>
                        {editData?.image ? (
                            <img
                                src={editData.image}
                                alt="OCR Package"
                                className={styles.modalImage}
                            />
                        ) : (
                            <div className={styles.noImage}>No image available</div>
                        )}
                    </div>
                </div>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue }) => (
                    <Form className={styles.form}>
                        <div className={styles.field}>
                            <label>Description</label>
                            <Field name="description" type="text" />
                            <ErrorMessage name="description" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>LBs</label>
                            <Field name="quantity" type="number" min="1" />
                            <ErrorMessage name="quantity" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Package Name</label>
                            <Field name="packageName" type="text" />
                            <ErrorMessage name="packageName" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Merchant</label>
                            <Field name="merchant" type="text" />
                            <ErrorMessage name="merchant" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Bill To</label>
                            <Select
                                name="billTo"
                                options={allUsers}
                                value={allUsers.find((option) => option.value === values.billTo) || null}
                                onChange={(selectedOption) => handleBillToChange(selectedOption, setFieldValue)}
                                placeholder="Select user"
                            />
                            <ErrorMessage name="billTo" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Courier Name</label>
                            <Field name="courier">
                                {({ field }) => (
                                    <select {...field} onChange={(e) => handleCourierChange(e, setFieldValue)}>
                                        <option value="">Select Courier</option>
                                        {couriers.map((courier) => (
                                            <option key={courier._id} value={courier._id}>
                                                {courier?.courier?.businessName || `${courier?.name?.firstName} ${courier?.name?.lastName}`}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </Field>
                            <ErrorMessage name="courier" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Client Type</label>
                            <Field name="consigneeType">
                                {({ field }) => (
                                    <select {...field} onChange={(e) => handleTypeChange(e, setFieldValue)}>
                                        <option value="">Select Client type</option>
                                        <option value="client">Client</option>
                                        <option value="corporate">Corporate</option>
                                        <option value="reseller">Reseller</option>
                                        <option value="courier">Courier</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                )}
                            </Field>
                            <ErrorMessage name="consigneeType" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Tracking Number</label>
                            <Field name="trackingNumber" type="text" />
                            <ErrorMessage name="trackingNumber" component="div" className={styles.error} />
                        </div>

                        <button type="submit" className={styles.submitButton}>
                            Update OCR Package
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );
}

export default EditOCRPackage;